import html2canvas from "html2canvas";

async function withUnhiddenOverflow(node, capture) {
  const affected = [];
  const unhide = (el) => {
    const cs = getComputedStyle(el);
    if (
      ["auto", "scroll", "hidden"].includes(cs.overflow) ||
      ["auto", "scroll", "hidden"].includes(cs.overflowX) ||
      ["auto", "scroll", "hidden"].includes(cs.overflowY)
    ) {
      affected.push({
        el,
        overflow: el.style.overflow,
        overflowX: el.style.overflowX,
        overflowY: el.style.overflowY,
      });
      el.style.overflow = "visible";
      el.style.overflowX = "visible";
      el.style.overflowY = "visible";
    }
  };

  node.querySelectorAll("*").forEach(unhide);
  // Ancestors (e.g. the dashboard's scrollable <main>) can clip the card too
  // when it sits below the fold, so unhide those as well as descendants.
  for (let ancestor = node.parentElement; ancestor; ancestor = ancestor.parentElement) {
    unhide(ancestor);
  }

  try {
    return await capture();
  } finally {
    affected.forEach(({ el, overflow, overflowX, overflowY }) => {
      el.style.overflow = overflow;
      el.style.overflowX = overflowX;
      el.style.overflowY = overflowY;
    });
  }
}

function downloadCanvasAsPng(canvas, title) {
  const a = document.createElement("a");
  a.download = `${title.replace(/[\s/\\|:*?"<>]+/g, "_")}.png`;
  a.href = canvas.toDataURL("image/png");
  a.click();
}

export async function exportTablePng(sectionRef, title) {
  const section = sectionRef.current;
  if (!section) return;

  const btns = section.querySelectorAll("[data-export-btn]");
  btns.forEach((b) => (b.style.visibility = "hidden"));

  try {
    await withUnhiddenOverflow(section, async () => {
      const canvas = await html2canvas(section, {
        scale: 2,
        useCORS: true,
        allowTaint: true,
        backgroundColor: "#ffffff",
        logging: false,
        width: section.scrollWidth,
        height: section.scrollHeight,
        windowWidth: section.scrollWidth,
      });
      downloadCanvasAsPng(canvas, title);
    });
  } finally {
    btns.forEach((b) => (b.style.visibility = ""));
  }
}

export async function captureNodeAsPng(node, title) {
  node.querySelectorAll("[data-export-btn]").forEach((b) => b.remove());

  await withUnhiddenOverflow(node, async () => {
    const canvas = await html2canvas(node, {
      scale: 2,
      useCORS: true,
      allowTaint: true,
      backgroundColor: "#ffffff",
      logging: false,
      width: node.scrollWidth,
      height: node.scrollHeight,
      windowWidth: node.scrollWidth,
    });
    downloadCanvasAsPng(canvas, title);
  });
}

// The dashboard UI keeps multiple chart panels side by side in one card, but
// html2canvas cannot reliably rasterize two side-by-side Recharts SVGs in a
// single pass (bars/labels from one panel bleed into the neighbouring one).
// So exports clone the already-rendered header + a single panel's DOM into an
// isolated offscreen container and rasterize each panel on its own, without
// changing anything about how the card looks on screen.
export async function exportPanelsSeparately(headerRef, panels, baseTitle) {
  const headerNode = headerRef?.current;
  for (const { ref, suffix } of panels) {
    const panelNode = ref.current;
    if (!panelNode) continue;

    const container = document.createElement("div");
    container.style.position = "fixed";
    container.style.top = "0";
    container.style.left = "-10000px";
    container.style.width = `${Math.max(panelNode.getBoundingClientRect().width, 480)}px`;
    container.style.background = "#ffffff";
    container.className = "rounded-2xl border border-[#d6dfeb]";
    document.body.appendChild(container);

    if (headerNode) container.appendChild(headerNode.cloneNode(true));
    const body = document.createElement("div");
    body.className = "px-3 pt-3 pb-4";
    body.appendChild(panelNode.cloneNode(true));
    container.appendChild(body);

    try {
      await captureNodeAsPng(container, `${baseTitle}${suffix}`);
    } finally {
      container.remove();
    }
  }
}
